import {
	Box,
	Stack,
	Button,
	Text,
	Icons,
	Inline,
	Container,
	Table,
	SelectInput,
	Spinner,
	Tabs,
	TabsList,
	TabItem,
	TabContent,
	Alert,
} from "@sembark-travel/ui/base"
import { useLocationQuery } from "@sembark-travel/ui/router"
import {
	TSearchParams,
	useSearch,
	Search,
	areAdvancedFiltersAppliedDefault,
	ListView,
} from "@sembark-travel/ui/list"
import {
	dateToQuery,
	dateToUTCString,
	parseDateFromQuery,
	startOf,
	endOf,
	subtractUnit,
	addUnit,
	isAfter,
} from "@sembark-travel/datetime-utils"
import { IListResponse, XHRInstance } from "@sembark-travel/xhr"
import { useEffect, useMemo } from "react"
import { $PropertyType } from "utility-types"
import config from "../config"
import { SelectTripOwners } from "../Trips"
import { IUser } from "../Users"
import { SelectField } from "@sembark-travel/ui/form"
import { TemporaryStorage } from "../TemporaryStorage"
import {
	LeadStatusType,
	TTripPlanRequestReportStats,
	useTripPlanRequestRedirect,
	useTripPlanRequestReportStats,
} from "./store"
import { IntervalTitle } from "../TripSalesReports"
import { SelectLeadSources } from "./List"

export type TTypeWiseReportItem = {
	id: number
	name: string
	email: string
} & TTripPlanRequestReportStats

function ReportXHR(xhr: XHRInstance) {
	return {
		async getUserReport(params?: unknown) {
			return xhr
				.get<
					IListResponse<TTypeWiseReportItem>
				>("/trip-plan-request-report/stats-by-user", { params })
				.then((data) => data.data)
		},
		async getSourceReport(params?: unknown) {
			return xhr
				.get<
					IListResponse<TTypeWiseReportItem>
				>("/trip-plan-request-report/stats-by-source", { params })
				.then((data) => data.data)
		},
	}
}

type TFilters = TSearchParams & {
	after: Date
	before: Date
	trip_sources?: Array<{ name: string; id: string }>
	owners?: Array<Pick<IUser, "id" | "name">>
	intervalType: "month" | "week" | "day"
	type: "users" | "sources"
}

type TFiltersInQuery = TSearchParams & {
	after?: string
	before?: string
	trip_sources?: Array<string>
	owners?: Array<string>
	intervalType: "month" | "week" | "day"
	type?: "users" | "sources"
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const { q, after, before, page, trip_sources, owners, intervalType, type } =
		filters
	const query: TFiltersInQuery = {
		after: dateToQuery(after || startOf(new Date(), "month")),
		before: dateToQuery(before || endOf(new Date(), "month")),
		intervalType: intervalType || "month",
		type: type || "sources",
	}
	if (q) {
		query.q = q
	}
	if (page) query.page = page
	if (trip_sources) {
		query.trip_sources = trip_sources.map((v) => v.id)
	}
	if (owners) {
		query.owners = owners.map((d) => `${d.id}_${d.name}`)
	}
	return query
}

function queryToFilters(query: TFiltersInQuery): TFilters {
	const { q, page, trip_sources, owners, after, before, intervalType, type } =
		query
	const filters: TFilters = {
		after: after
			? startOf(parseDateFromQuery(after), "day")
			: startOf(new Date(), intervalType || "month"),
		before: before
			? endOf(parseDateFromQuery(before), "day")
			: endOf(new Date(), intervalType || "month"),
		intervalType: intervalType || "month",
		type: type || "sources",
	}
	if (q) {
		filters.q = q
	}
	if (page) filters.page = page

	if (trip_sources?.length) {
		filters.trip_sources = trip_sources.map((v) => {
			return { name: v, id: v }
		})
	}
	if (owners) {
		filters.owners = owners.map((d) => {
			const [id, ...name] = d.split("_")
			return {
				id,
				name: name.join("_"),
			}
		}) as unknown as $PropertyType<TFilters, "owners">
	}
	return filters
}

export function TripPlanRequestReportsList() {
	const [query, setQuery] = useLocationQuery({
		toQuery: filtersToQuery,
		fromQuery: queryToFilters,
	})
	const [params, setParams] = useSearch<TFilters>(query)
	const requestParams = useMemo(() => {
		return {
			q: params.q || null,
			trip_sources: params?.trip_sources?.map((v) => v.id),
			owners: (params.owners || []).map((d) => d.id),
			created_after: dateToUTCString(params.after),
			created_before: dateToUTCString(params.before),
		}
	}, [params])
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const { stats, error: errorInStats } = useTripPlanRequestReportStats({
		params: requestParams,
	})
	return (
		<Search
			title="Trip Plan Request Report"
			initialParams={params}
			onSearch={setParams}
			Filters={Filters}
			resetParams={(params) => ({
				q: "",
				after: params.after,
				before: params.before,
				intervalType: params.intervalType,
				type: params.type,
			})}
			areAdvancedFiltersApplied={(params) => {
				const { after, before, intervalType, type, ...others } = params
				return areAdvancedFiltersAppliedDefault(others)
			}}
			actions={({ setSearchParams, searchParams }) => (
				<Inline alignItems="center">
					<Button
						onClick={() => {
							const after = startOf(
								subtractUnit(searchParams.after, 1, searchParams.intervalType),
								searchParams.intervalType
							)
							setSearchParams({
								after,
								before: endOf(after, searchParams.intervalType),
							})
						}}
						aria-label="Previous interval"
					>
						<Icons.ChevronDown rotate="90" />
					</Button>
					<Box paddingX="2">
						<SelectInput
							value={searchParams.intervalType}
							onChange={(e) => {
								const type = (e.currentTarget.value ||
									"month") as typeof searchParams.intervalType
								setSearchParams({
									intervalType: type,
									after: startOf(new Date(), type),
									before: endOf(new Date(), type),
								})
							}}
						>
							<option value="month">Month</option>
							<option value="week">Week</option>
							<option value="day">Day</option>
						</SelectInput>
					</Box>
					<Button
						disabled={isAfter(searchParams.before, new Date())}
						onClick={() => {
							const after = startOf(
								addUnit(searchParams.after, 1, searchParams.intervalType),
								searchParams.intervalType
							)
							setSearchParams({
								after,
								before: endOf(after, searchParams.intervalType),
							})
						}}
						aria-label="Next interval"
					>
						<Icons.ChevronDown rotate="270" />
					</Button>
				</Inline>
			)}
		>
			{({ searchParams, setSearchParamValue }) => (
				<Stack gap="6" paddingY="4">
					<Container>
						<Stack gap="4">
							{errorInStats ? (
								<Alert status="error">
									{errorInStats.message || "Something went wrong."}
								</Alert>
							) : null}
							{stats ? (
								<TripPlanRequestReportStats
									after={params.after}
									before={params.before}
									intervalType={params.intervalType}
									stats={stats}
									params={params}
								/>
							) : (
								<Box
									paddingY="8"
									bgColor="default"
									width="full"
									textAlign="center"
									rounded="md"
								>
									<Spinner alignCenter />
								</Box>
							)}
						</Stack>
					</Container>
					{stats && stats.total ? (
						<Tabs>
							<TabsList>
								<TabItem
									active={searchParams.type === "sources"}
									onClick={() => {
										setSearchParamValue("type", "sources")
									}}
								>
									Trip Sources
								</TabItem>
								<TabItem
									active={searchParams.type === "users"}
									onClick={() => {
										setSearchParamValue("type", "users")
									}}
								>
									Sales Person
								</TabItem>
							</TabsList>

							<TabContent>
								<TypeWiseListView
									type={params.type}
									params={params}
									setParams={setParams}
									requestParams={requestParams}
								/>
							</TabContent>
						</Tabs>
					) : null}
				</Stack>
			)}
		</Search>
	)
}

function Filters() {
	return (
		<Stack gap="4">
			<SelectField
				select={SelectLeadSources}
				name="trip_sources"
				label="Trip Sources"
				multiple
				fetchOnMount
			/>
			<SelectField
				select={SelectTripOwners}
				name="owners"
				label="Sales Person"
				multiple
				fetchOnMount
			/>
		</Stack>
	)
}

function TypeWiseListView({
	type,
	params,
	setParams,
	requestParams,
}: {
	type: TFilters["type"]
	params: TFilters
	setParams: (params: TFilters) => void
	requestParams: Record<string, unknown>
}) {
	const { onRedirect } =
		useTripPlanRequestRedirect<Omit<TFiltersInQuery, "intervalType">>()

	return (
		<ListView<TTypeWiseReportItem, TFilters>
			pageKey={`trip-sales-reports-${type}`}
			params={params}
			onPageChange={(page) => setParams({ ...params, page })}
			fetch={(xhr, { trip_sources, ...params }) => {
				const payload = {
					...params,
					trip_sources: trip_sources?.map((v) => v.id) || [],
					owners: (params.owners || []).map((d) => d.id),
					created_after: dateToUTCString(params.after),
					created_before: dateToUTCString(params.before),
				}
				return type === "sources"
					? ReportXHR(xhr).getSourceReport(payload)
					: ReportXHR(xhr).getUserReport(payload)
			}}
			actions={
				<TemporaryStorage
					initUrl={
						type === "sources"
							? "/trip-plan-request-report/stats-by-source/download"
							: "/trip-plan-request-report/stats-by-user/download"
					}
					initParams={{
						...requestParams,
						timezone_offset: config.timezoneOffset,
					}}
				>
					{({ message, generate, url, reset, isGenerating }) =>
						url ? (
							<Button
								as="a"
								href={url}
								target="_blank"
								download
								level="primary"
								size="sm"
								onClick={() => reset()}
							>
								{message}
							</Button>
						) : (
							<Button
								size="sm"
								onClick={generate}
								disabled={isGenerating}
								level="primary"
							>
								{message || (
									<>
										<Icons.DocumentDownload /> Full Report
									</>
								)}
							</Button>
						)
					}
				</TemporaryStorage>
			}
		>
			{({ items }) => {
				return (
					<Table
						bordered
						hover
						responsive
						headers={[
							"Name",
							"Total",
							"Active Follow-Up",
							"Pending Follow-Up",
							"Moved To Trips",
							"Archived",
						]}
						rows={items.map(
							({
								name,
								moved_to_trips,
								archived,
								active_followups,
								pending_followups,
								id,
								total,
							}) => [
								<Box
									cursor="pointer"
									onClick={() => {
										const query = {
											ca: dateToQuery(params.after),
											cb: dateToQuery(params.before),
											source:
												type === "sources" && !params?.trip_sources?.length
													? name
													: "all",
											trip_sources: params?.trip_sources?.map((v) => v.id),
											owners:
												type === "users"
													? [`${id}-${name}`]
													: params?.owners?.map(
															(user) => `${user.id}-${user.name}`
														),
											leadStatus: LeadStatusType.map(
												(type) => `${type.id}-${type.name}`
											),
										}
										onRedirect(query)
									}}
								>
									<Text>{name}</Text>
								</Box>,
								<Box
									cursor="pointer"
									onClick={() => {
										const query = {
											ca: dateToQuery(params.after),
											cb: dateToQuery(params.before),
											source:
												type === "sources" && !params?.trip_sources?.length
													? name
													: "all",
											trip_sources: params?.trip_sources?.map((v) => v.id),
											owners:
												type === "users"
													? [`${id}-${name}`]
													: params?.owners?.map(
															(user) => `${user.id}-${user.name}`
														),
											leadStatus: LeadStatusType.map(
												(type) => `${type.id}-${type.name}`
											),
										}
										onRedirect(query)
									}}
								>
									<Text>{total}</Text>
								</Box>,
								<Text>{active_followups}</Text>,
								<Text>{pending_followups}</Text>,
								<Text>{moved_to_trips}</Text>,
								<Text>{archived}</Text>,
							]
						)}
					/>
				)
			}}
		</ListView>
	)
}

function TripPlanRequestReportStats({
	stats,
	after,
	before,
	intervalType,
	params,
}: {
	stats: TTripPlanRequestReportStats
	params: TFilters
} & Pick<TFilters, "after" | "before" | "intervalType">) {
	const {
		archived,
		moved_to_trips,
		pending_followups,
		total,
		active_followups,
	} = stats
	const { onRedirect } =
		useTripPlanRequestRedirect<Omit<TFiltersInQuery, "intervalType">>()
	return (
		<Inline
			bgColor="default"
			roundedRight="lg"
			borderLeftWidth="4"
			borderColor="accent"
			paddingY="2"
			justifyContent="between"
			alignItems="stretch"
			collapseBelow="md"
			gap="4"
		>
			<Inline paddingLeft={"2"} gap="2" alignItems="center" collapseBelow="sm">
				<Box
					paddingX={{ xs: "4", sm: "8" }}
					paddingY="4"
					borderRightWidth={{ sm: "4" }}
					borderBottomWidth={{ xs: "4", sm: "0" }}
					borderColor="default"
				>
					<IntervalTitle
						after={after}
						before={before}
						intervalType={intervalType}
					/>
				</Box>

				<Inline
					gap={{ xs: "4", sm: "8" }}
					paddingY="4"
					paddingX={{ xs: "4", sm: "8" }}
					flexWrap={"wrap"}
					justifyContent={"center"}
				>
					<Stack
						cursor="pointer"
						onClick={() => {
							const query = {
								ca: dateToQuery(params.after),
								cb: dateToQuery(params.before),
								source: "all",
								trip_sources: params?.trip_sources?.map((v) => v.id),
								owners: params?.owners?.map(
									(user) => `${user.id}-${user.name}`
								),
								leadStatus: LeadStatusType.map(
									(type) => `${type.id}-${type.name}`
								),
							}
							onRedirect(query)
						}}
						paddingX={{ xs: "4", sm: "8" }}
					>
						<Text fontWeight="semibold" color="muted">
							Total Lead
						</Text>
						<Box textAlign={"center"} fontWeight="semibold" fontSize="3xl">
							<Text>{total}</Text>
						</Box>
					</Stack>
					<Stack
						cursor="pointer"
						onClick={() => {
							const query = {
								ca: dateToQuery(params.after),
								cb: dateToQuery(params.before),
								source: "all",
								trip_sources: params?.trip_sources?.map((v) => v.id),
								owners: params?.owners?.map(
									(user) => `${user.id}-${user.name}`
								),
								leadStatus: ["active_followups-Active Followup"],
							}
							onRedirect(query)
						}}
					>
						<Text fontWeight="semibold" color="muted">
							Active Follow-Up
						</Text>
						<Text textAlign={"center"} fontWeight="semibold" fontSize="3xl">
							{active_followups}
						</Text>
					</Stack>
					<Stack
						cursor="pointer"
						onClick={() => {
							const query = {
								ca: dateToQuery(params.after),
								cb: dateToQuery(params.before),
								source: "all",
								trip_sources: params?.trip_sources?.map((v) => v.id),
								owners: params?.owners?.map(
									(user) => `${user.id}-${user.name}`
								),
								leadStatus: ["pending_followups-Pending Followup"],
							}
							onRedirect(query)
						}}
					>
						<Text fontWeight="semibold" color="muted">
							Pending Follow-Up
						</Text>
						<Text textAlign={"center"} fontWeight="semibold" fontSize="3xl">
							{pending_followups}
						</Text>
					</Stack>
					<Stack
						cursor="pointer"
						onClick={() => {
							const query = {
								ca: dateToQuery(params.after),
								cb: dateToQuery(params.before),
								source: "all",
								trip_sources: params?.trip_sources?.map((v) => v.id),
								owners: params?.owners?.map(
									(user) => `${user.id}-${user.name}`
								),
								leadStatus: ["moved-Moved"],
							}
							onRedirect(query)
						}}
					>
						<Text fontWeight="semibold" color="muted">
							Moved To Trips
						</Text>
						<Text textAlign={"center"} fontWeight="semibold" fontSize="3xl">
							{moved_to_trips}
						</Text>
					</Stack>
					<Stack
						cursor="pointer"
						onClick={() => {
							const query = {
								ca: dateToQuery(params.after),
								cb: dateToQuery(params.before),
								source: "all",
								trip_sources: params?.trip_sources?.map((v) => v.id),
								owners: params?.owners?.map(
									(user) => `${user.id}-${user.name}`
								),
								leadStatus: ["archived-Archived"],
							}
							onRedirect(query)
						}}
					>
						<Text fontWeight="semibold" color="muted">
							Archived
						</Text>
						<Text textAlign={"center"} fontWeight="semibold" fontSize="3xl">
							{archived}
						</Text>
					</Stack>
				</Inline>
			</Inline>
		</Inline>
	)
}
